import { servicesIn, type DateRange, type Insight } from './community';
import { addDays, MONTH_LONG, MONTH_SHORT, monthKey, normalize } from './dates';
import { LEGAL_VERSION } from './legal';
import { memberPhoneError, normalizeEmail, phoneDigits } from './members';
import { emptyRegistrationData } from './registrations';
import type {
  AgeRange,
  ContactOutcome,
  FollowUpKind,
  FollowUpStatus,
  JourneyStage,
  Registration,
  ScheduleEvent,
  Settings,
  Visitor,
  VisitorContact,
  VisitorFollowUp,
  VisitorSource,
  VisitorVisit,
} from './types';

/**
 * Visitantes e integração (Membresia): a **ficha** opcional de quem visitou, com
 * consentimento e coleta mínima, e a jornada de integração — etapas **administrativas** e
 * configuráveis, que organizam tarefas e não medem fé nem compromisso. Não pressupõe que
 * todo visitante vá virar membro: arquivar é uma saída normal.
 *
 * A ficha não se confunde com a contagem do culto (`ServiceAudience.firstTime` e
 * `recurring`): a contagem nunca cria ficha, e as telas dizem sempre qual número é qual.
 */

// --- Catálogos ---------------------------------------------------------------------------

export const AGE_RANGES: { value: AgeRange; label: string }[] = [
  { value: '18-29', label: '18 a 29 anos' },
  { value: '30-44', label: '30 a 44 anos' },
  { value: '45-59', label: '45 a 59 anos' },
  { value: '60+', label: '60 anos ou mais' },
  { value: 'unknown', label: 'Não informada' },
];

export const VISITOR_SOURCES: { value: VisitorSource; label: string }[] = [
  { value: 'invite', label: 'Convite de amigo ou familiar' },
  { value: 'social', label: 'Redes sociais' },
  { value: 'passing', label: 'Passou em frente' },
  { value: 'event', label: 'Evento especial' },
  { value: 'online', label: 'Transmissão online' },
  { value: 'other', label: 'Outro' },
];

export const FOLLOW_UP_KINDS: { value: FollowUpKind; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Ligação' },
  { value: 'visit', label: 'Visita' },
  { value: 'message', label: 'Mensagem' },
  { value: 'other', label: 'Outro' },
];

export const FOLLOW_UP_STATUS_LABEL: Record<FollowUpStatus, string> = {
  pending: 'Pendente',
  doing: 'Em andamento',
  done: 'Concluído',
};

export const CONTACT_OUTCOMES: { value: ContactOutcome; label: string }[] = [
  { value: 'reached', label: 'Conversamos' },
  { value: 'noAnswer', label: 'Não atendeu' },
  { value: 'declined', label: 'Não quer mais contato' },
];

export const labelOf = <T extends string>(list: readonly { value: T; label: string }[], value: T): string =>
  list.find((x) => x.value === value)?.label ?? value;

/** Etapas fixas: a entrada, o encaminhamento ao cadastro e o arquivo. */
export const STAGE_NEW = 'new';
export const STAGE_REGISTERED = 'registered';
export const STAGE_ARCHIVED = 'archived';
export const FIXED_STAGES = [STAGE_NEW, STAGE_REGISTERED, STAGE_ARCHIVED] as const;

/** A jornada padrão; as do meio se renomeiam e se desligam em Configurações. */
export const DEFAULT_JOURNEY: JourneyStage[] = [
  { key: STAGE_NEW, label: 'Novo', active: true },
  { key: 'contacted', label: 'Contatado', active: true },
  { key: 'returned', label: 'Retornou', active: true },
  { key: 'integrating', label: 'Em integração', active: true },
  { key: STAGE_REGISTERED, label: 'Cadastro enviado', active: true },
  { key: STAGE_ARCHIVED, label: 'Arquivado', active: true },
];

export const DEFAULT_VISITOR_RETENTION = 12;
export const MIN_VISITOR_RETENTION = 3;
export const MAX_VISITOR_RETENTION = 24;
export const MAX_VISITOR_NOTE = 140;
export const MAX_STAGE_LABEL = 30;
/** Abaixo de tantas fichas, a taxa de retorno não aparece ("Sem registros suficientes"). */
export const MIN_RETURN_BASE = 5;

export const VISITOR_CONSENT_TEXT =
  'Autorizo a igreja a guardar estes dados e a entrar em contato comigo pelos canais informados, ' +
  'conforme a Política de Privacidade. A ficha é anonimizada depois de 12 meses sem visita nem contato, ' +
  'e posso pedir a exclusão a qualquer momento.';

export const VISITOR_STAFF_CONSENT =
  'A pessoa autorizou o registro destes dados e o contato da igreja (ela pode pedir a exclusão a qualquer momento).';

// --- Jornada -----------------------------------------------------------------------------

/** As etapas ativas, na ordem do quadro (as fixas sempre aparecem). */
export function journeyOf(settings: Pick<Settings, 'visitorJourney'>): JourneyStage[] {
  const fixed = new Set<string>(FIXED_STAGES);

  return settings.visitorJourney.filter((s) => s.active || fixed.has(s.key));
}

export function stageLabel(settings: Pick<Settings, 'visitorJourney'>, key: string): string {
  return settings.visitorJourney.find((s) => s.key === key)?.label ?? 'Novo';
}

/** A etapa da ficha no quadro: etapa desligada (ou que não existe mais) volta a "Novo". */
export function boardStage(settings: Pick<Settings, 'visitorJourney'>, visitor: Visitor): string {
  return journeyOf(settings).some((s) => s.key === visitor.stage) ? visitor.stage : STAGE_NEW;
}

/** Erro da jornada editada: nomes vazios, longos ou repetidos, ou uma fixa faltando. */
export function journeyError(journey: readonly JourneyStage[]): string | null {
  for (const key of FIXED_STAGES) {
    if (!journey.some((s) => s.key === key)) return 'As etapas Novo, Cadastro enviado e Arquivado são fixas.';
  }

  const names = new Set<string>();

  for (const s of journey) {
    const label = s.label.trim();

    if (!label) return 'Dê um nome a cada etapa.';
    if (label.length > MAX_STAGE_LABEL) return `O nome da etapa vai até ${MAX_STAGE_LABEL} caracteres.`;
    if (names.has(normalize(label))) return `Há duas etapas chamadas "${label}".`;

    names.add(normalize(label));
  }

  return null;
}

// --- Ficha -------------------------------------------------------------------------------

/** O formulário da ficha (equipe ou link público), como se digita. */
export interface VisitorDraft {
  name: string;
  phone: string;
  email: string;
  ageRange: AgeRange;
  district: string;
  source: VisitorSource;
  invitedById: string | null;
  wantsContact: boolean;
  wantsMembership: boolean;
  note: string;
  /** Consentimento: o aceite do visitante (link) ou o registro de quem atendeu (equipe). */
  consent: boolean;
  /** A primeira visita (só na criação). */
  visit: VisitorVisit;
}

export function emptyVisitorDraft(today: string, eventId: string | null = null): VisitorDraft {
  return {
    name: '',
    phone: '',
    email: '',
    ageRange: 'unknown',
    district: '',
    source: 'invite',
    invitedById: null,
    wantsContact: true,
    wantsMembership: false,
    note: '',
    consent: false,
    visit: { date: today, eventId },
  };
}

export function visitorDraftOf(visitor: Visitor): VisitorDraft {
  return {
    name: visitor.name,
    phone: visitor.phone,
    email: visitor.email,
    ageRange: visitor.ageRange,
    district: visitor.district,
    source: visitor.source,
    invitedById: visitor.invitedById,
    wantsContact: visitor.wantsContact,
    wantsMembership: visitor.wantsMembership,
    note: visitor.note,
    consent: true,
    visit: visitor.visits[0] ?? { date: '', eventId: null },
  };
}

export type VisitorField = 'name' | 'phone' | 'email' | 'note' | 'consent' | 'visit';
export type VisitorErrors = Partial<Record<VisitorField, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** As regras do Salvar: nome, contato válido (obrigatório com "quer contato"), consentimento. */
export function visitorErrors(draft: VisitorDraft, today: string): VisitorErrors {
  const errors: VisitorErrors = {};
  const name = draft.name.trim();
  const phone = phoneDigits(draft.phone);
  const email = normalizeEmail(draft.email);

  if (name.length < 2) errors.name = 'Informe o nome.';
  else if (name.length > 120) errors.name = 'O nome vai até 120 caracteres.';

  if (phone) {
    const error = memberPhoneError(phone);

    if (error) errors.phone = error;
  }

  if (email && !EMAIL.test(email)) errors.email = 'E-mail inválido.';
  if (draft.wantsContact && !phone && !email)
    errors.phone = 'Para receber contato, informe o celular ou o e-mail.';
  if (draft.note.trim().length > MAX_VISITOR_NOTE) {
    errors.note = `A observação vai até ${MAX_VISITOR_NOTE} caracteres.`;
  }
  if (!draft.consent) errors.consent = 'Sem o consentimento, a ficha não é registrada.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.visit.date)) errors.visit = 'Informe o dia da visita.';
  else if (draft.visit.date > today) errors.visit = 'A visita não pode ser no futuro.';

  return errors;
}

function normalizeDraft(draft: VisitorDraft) {
  return {
    name: draft.name.trim().replace(/\s+/g, ' '),
    phone: phoneDigits(draft.phone),
    email: normalizeEmail(draft.email),
    ageRange: draft.ageRange,
    district: draft.district.trim(),
    source: draft.source,
    invitedById: draft.invitedById,
    wantsContact: draft.wantsContact,
    wantsMembership: draft.wantsMembership,
    note: draft.note.trim(),
  };
}

/** A ficha que já é desta pessoa (mesmo celular ou e-mail), para não duplicar. */
export function visitorMatch(
  visitors: readonly Visitor[],
  contact: { phone: string; email: string }
): Visitor | undefined {
  const phone = phoneDigits(contact.phone);
  const email = normalizeEmail(contact.email);

  return visitors.find(
    (v) => !v.anonymized && ((phone && v.phone === phone) || (email && v.email === email))
  );
}

export function firstVisit(visitor: Visitor): string {
  return visitor.visits[0]?.date ?? '';
}

export function lastVisit(visitor: Visitor): string {
  return visitor.visits.at(-1)?.date ?? '';
}

const sortVisits = (visits: VisitorVisit[]) =>
  [...visits].sort(
    (a, b) => a.date.localeCompare(b.date) || (a.eventId ?? '').localeCompare(b.eventId ?? '')
  );

// --- Transições --------------------------------------------------------------------------

export interface VisitorContext {
  who: string;
  at: number;
}

/** Nova ficha registrada pela equipe (o consentimento é o de viva voz). */
export function addVisitor(
  visitors: Visitor[],
  id: string,
  draft: VisitorDraft,
  ctx: VisitorContext & { origin?: Visitor['origin']; ownerId?: string | null }
): Visitor[] {
  const origin = ctx.origin ?? 'staff';

  return [
    ...visitors,
    {
      id,
      ...normalizeDraft(draft),
      visits: [draft.visit],
      stage: STAGE_NEW,
      ownerId: ctx.ownerId ?? null,
      consent: { at: ctx.at, version: LEGAL_VERSION, by: origin === 'link' ? 'self' : 'staff' },
      origin,
      registrationId: null,
      followUps: [],
      contacts: [],
      archivedAt: null,
      anonymized: false,
      createdAt: ctx.at,
      createdBy: ctx.who,
    },
  ];
}

/**
 * Ficha enviada pelo link público. Se a pessoa já tem ficha (mesmo celular ou e-mail),
 * soma a visita e atualiza o que ela informou de novo, sem duplicar; a ficha arquivada
 * volta a "Novo".
 */
export function submitVisitorForm(
  visitors: Visitor[],
  id: string,
  draft: VisitorDraft,
  at: number
): { visitors: Visitor[]; id: string; matched: boolean } {
  const match = visitorMatch(visitors, draft);

  if (!match) {
    return {
      visitors: addVisitor(
        visitors,
        id,
        { ...draft, invitedById: null, note: '' },
        { who: '', at, origin: 'link' }
      ),
      id,
      matched: false,
    };
  }

  const d = normalizeDraft(draft);
  const next = visitors.map((v) =>
    v.id === match.id
      ? {
          ...recordVisitOn(v, draft.visit),
          name: d.name,
          phone: d.phone || v.phone,
          email: d.email || v.email,
          ageRange: d.ageRange === 'unknown' ? v.ageRange : d.ageRange,
          district: d.district || v.district,
          wantsContact: d.wantsContact,
          wantsMembership: v.wantsMembership || d.wantsMembership,
          consent: { at, version: LEGAL_VERSION, by: 'self' as const },
          ...(v.stage === STAGE_ARCHIVED ? { stage: STAGE_NEW, archivedAt: null } : {}),
        }
      : v
  );

  return { visitors: next, id: match.id, matched: true };
}

/** Edita os dados da ficha (não mexe nas visitas, na etapa nem nos acompanhamentos). */
export function updateVisitor(visitors: Visitor[], id: string, draft: VisitorDraft): Visitor[] {
  return visitors.map((v) => (v.id === id && !v.anonymized ? { ...v, ...normalizeDraft(draft) } : v));
}

function recordVisitOn(visitor: Visitor, visit: VisitorVisit): Visitor {
  const same = visitor.visits.some((x) => x.date === visit.date && x.eventId === visit.eventId);

  return same ? visitor : { ...visitor, visits: sortVisits([...visitor.visits, visit]) };
}

/** "Registrar visita": a mesma visita (dia e culto) não se repete. */
export function recordVisit(visitors: Visitor[], id: string, visit: VisitorVisit): Visitor[] {
  return visitors.map((v) => (v.id === id && !v.anonymized ? recordVisitOn(v, visit) : v));
}

export function removeVisit(visitors: Visitor[], id: string, visit: VisitorVisit): Visitor[] {
  return visitors.map((v) =>
    v.id === id && v.visits.length > 1
      ? { ...v, visits: v.visits.filter((x) => !(x.date === visit.date && x.eventId === visit.eventId)) }
      : v
  );
}

/** Move a ficha de etapa; arquivar guarda quando, e sair do arquivo limpa. */
export function moveVisitor(visitors: Visitor[], id: string, stage: string, at: number): Visitor[] {
  return visitors.map((v) => {
    if (v.id !== id || v.anonymized || v.stage === stage) return v;

    return { ...v, stage, archivedAt: stage === STAGE_ARCHIVED ? at : null };
  });
}

export function assignOwner(visitors: Visitor[], id: string, ownerId: string | null): Visitor[] {
  return visitors.map((v) => (v.id === id ? { ...v, ownerId } : v));
}

export type FollowUpDraft = Pick<VisitorFollowUp, 'kind' | 'ownerId' | 'due' | 'note'>;

export function followUpError(draft: FollowUpDraft): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.due)) return 'Informe o prazo.';
  if (draft.note.trim().length > MAX_VISITOR_NOTE)
    return `A observação vai até ${MAX_VISITOR_NOTE} caracteres.`;

  return null;
}

/** Novo acompanhamento (pendente). Sem responsável na ficha, quem for responsável por ele passa a ser. */
export function addFollowUp(
  visitors: Visitor[],
  visitorId: string,
  followUpId: string,
  draft: FollowUpDraft
): Visitor[] {
  return visitors.map((v) =>
    v.id === visitorId && !v.anonymized
      ? {
          ...v,
          ownerId: v.ownerId ?? draft.ownerId,
          followUps: [
            ...v.followUps,
            {
              id: followUpId,
              ...draft,
              note: draft.note.trim(),
              status: 'pending',
              doneAt: null,
              doneBy: '',
            },
          ],
        }
      : v
  );
}

/** Edita ou muda a situação do acompanhamento; concluir guarda quem e quando. */
export function updateFollowUp(
  visitors: Visitor[],
  visitorId: string,
  followUpId: string,
  patch: Partial<FollowUpDraft & { status: FollowUpStatus }>,
  ctx: VisitorContext
): Visitor[] {
  return visitors.map((v) => {
    if (v.id !== visitorId) return v;

    return {
      ...v,
      followUps: v.followUps.map((f) => {
        if (f.id !== followUpId) return f;

        const status = patch.status ?? f.status;
        // Concluir guarda quem e quando; reabrir apaga; o já concluído fica como estava.
        const closing = status === 'done' && f.status !== 'done';
        const stamp =
          status === 'done'
            ? { doneAt: closing ? ctx.at : f.doneAt, doneBy: closing ? ctx.who : f.doneBy }
            : null;

        return {
          ...f,
          ...patch,
          note: (patch.note ?? f.note).trim(),
          status,
          doneAt: stamp?.doneAt ?? null,
          doneBy: stamp?.doneBy ?? '',
        };
      }),
    };
  });
}

export function removeFollowUp(visitors: Visitor[], visitorId: string, followUpId: string): Visitor[] {
  return visitors.map((v) =>
    v.id === visitorId ? { ...v, followUps: v.followUps.filter((f) => f.id !== followUpId) } : v
  );
}

/**
 * Registra um contato. "Não quer mais contato" desliga o contato, encerra os
 * acompanhamentos abertos e arquiva a ficha. O primeiro contato de uma ficha nova a leva
 * para "Contatado", quando essa etapa está ativa.
 */
export function logContact(
  visitors: Visitor[],
  visitorId: string,
  contact: Omit<VisitorContact, 'by' | 'at'>,
  ctx: VisitorContext & { journey?: readonly JourneyStage[] }
): Visitor[] {
  return visitors.map((v) => {
    if (v.id !== visitorId || v.anonymized) return v;

    const contacts = [...v.contacts, { ...contact, at: ctx.at, by: ctx.who }];

    if (contact.outcome === 'declined') {
      return {
        ...v,
        contacts,
        wantsContact: false,
        stage: STAGE_ARCHIVED,
        archivedAt: ctx.at,
        followUps: v.followUps.map((f) =>
          f.status === 'done' ? f : { ...f, status: 'done' as const, doneAt: ctx.at, doneBy: ctx.who }
        ),
      };
    }

    const contacted = ctx.journey?.find((s) => s.key === 'contacted' && s.active);
    const stage =
      v.stage === STAGE_NEW && contact.outcome === 'reached' && contacted ? contacted.key : v.stage;

    return { ...v, contacts, stage };
  });
}

// --- Cadastro de membro ------------------------------------------------------------------

/**
 * "Encaminhar para o cadastro": abre a solicitação de cadastro de membro (pendente, como as
 * do link público) com o que a ficha já tem; o Administrador completa e aprova em Membros.
 */
export function referToRegistration(
  state: { visitors: Visitor[]; registrations: Registration[] },
  visitorId: string,
  registrationId: string,
  at: number
): { visitors: Visitor[]; registrations: Registration[] } {
  const visitor = state.visitors.find((v) => v.id === visitorId);

  if (!visitor || visitor.anonymized || visitor.registrationId) return state;

  const data = emptyRegistrationData();

  return {
    registrations: [
      ...state.registrations,
      {
        id: registrationId,
        data: {
          ...data,
          name: visitor.name,
          phone: visitor.phone,
          email: visitor.email,
          address: { ...data.address, district: visitor.district },
          notes: 'Encaminhado pela ficha de visitante.',
        },
        status: 'pending',
        submittedAt: at,
        decidedAt: null,
        decidedBy: '',
        reason: '',
        memberId: null,
        linked: false,
        mergedFields: [],
        photo: null,
      },
    ],
    visitors: state.visitors.map((v) =>
      v.id === visitorId ? { ...v, registrationId, stage: STAGE_REGISTERED, archivedAt: null } : v
    ),
  };
}

export function referralError(visitor: Visitor): string | null {
  if (visitor.anonymized) return 'A ficha foi anonimizada.';
  if (visitor.registrationId) return 'A ficha já foi encaminhada para o cadastro.';

  return null;
}

/** O membro criado a partir da ficha (o cadastro encaminhado foi aprovado), ou `null`. */
export function visitorMemberId(visitor: Visitor, registrations: readonly Registration[]): string | null {
  if (!visitor.registrationId) return null;

  const registration = registrations.find((r) => r.id === visitor.registrationId);

  return registration?.status === 'approved' ? registration.memberId : null;
}

/** A ficha de onde veio o cadastro (o encaminhado ou, pelo celular/e-mail, o do link). */
export function registrationVisitor(
  registration: Registration,
  visitors: readonly Visitor[]
): { visitor: Visitor; linked: boolean } | null {
  const linked = visitors.find((v) => v.registrationId === registration.id);

  if (linked) return { visitor: linked, linked: true };

  const match = visitorMatch(
    visitors.filter((v) => !v.registrationId),
    registration.data
  );

  return match ? { visitor: match, linked: false } : null;
}

/** Liga um cadastro do link à ficha da mesma pessoa (a tela sugere; quem aprova confirma). */
export function linkRegistration(visitors: Visitor[], visitorId: string, registrationId: string): Visitor[] {
  return visitors.map((v) =>
    v.id === visitorId && !v.registrationId ? { ...v, registrationId, stage: STAGE_REGISTERED } : v
  );
}

// --- Acompanhamentos ---------------------------------------------------------------------

export interface FollowUpItem {
  visitor: Visitor;
  followUp: VisitorFollowUp;
  overdue: boolean;
}

const isOpen = (f: VisitorFollowUp) => f.status !== 'done';

/** Os acompanhamentos abertos (das fichas não arquivadas), do prazo mais curto ao mais longo. */
export function openFollowUps(visitors: readonly Visitor[], today: string): FollowUpItem[] {
  return visitors
    .filter((v) => !v.anonymized && v.stage !== STAGE_ARCHIVED)
    .flatMap((visitor) =>
      visitor.followUps
        .filter(isOpen)
        .map((followUp) => ({ visitor, followUp, overdue: followUp.due < today }))
    )
    .sort(
      (a, b) => a.followUp.due.localeCompare(b.followUp.due) || a.visitor.name.localeCompare(b.visitor.name)
    );
}

export function overdueFollowUps(visitors: readonly Visitor[], today: string): FollowUpItem[] {
  return openFollowUps(visitors, today).filter((x) => x.overdue);
}

/** O próximo acompanhamento aberto da ficha, ou `undefined`. */
export function nextFollowUp(visitor: Visitor): VisitorFollowUp | undefined {
  return visitor.followUps.filter(isOpen).sort((a, b) => a.due.localeCompare(b.due))[0];
}

// --- Retenção e anonimização -------------------------------------------------------------

/** A última atividade da ficha: a visita ou o contato mais recente (`yyyy-mm-dd`). */
export function lastActivity(visitor: Visitor): string {
  const contact = visitor.contacts.reduce((max, c) => Math.max(max, c.at), 0);
  const contactDate = contact ? new Date(contact).toISOString().slice(0, 10) : '';
  const visit = lastVisit(visitor);

  return contactDate > visit ? contactDate : visit;
}

function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();

  date.setUTCDate(Math.min(d, last));

  return date.toISOString().slice(0, 10);
}

/** Quando a ficha será anonimizada (`retentionMonths` depois da última atividade). */
export function retentionDate(visitor: Visitor, retentionMonths: number): string {
  return addMonths(lastActivity(visitor), retentionMonths);
}

/** Já passou do prazo de guarda (fichas encaminhadas ao cadastro ficam: viraram outro registro). */
export function retentionDue(visitor: Visitor, retentionMonths: number, today: string): boolean {
  return !visitor.anonymized && !visitor.registrationId && retentionDate(visitor, retentionMonths) <= today;
}

/** O prazo está perto (30 dias): a ficha mostra "Será anonimizada em…". */
export function retentionSoon(visitor: Visitor, retentionMonths: number, today: string): boolean {
  return !retentionDue(visitor, retentionMonths, today) && !visitor.anonymized && !visitor.registrationId
    ? retentionDate(visitor, retentionMonths) <= addDays(today, 30)
    : false;
}

/**
 * Anonimiza a ficha: some o que identifica a pessoa (nome, contato, bairro, quem convidou,
 * observações, quem fez os contatos) e ficam as datas, a etapa, a origem e a faixa etária,
 * para a estatística. Os acompanhamentos abertos são encerrados.
 */
export function anonymizeVisitor(visitors: Visitor[], id: string, at: number): Visitor[] {
  const index = visitors.filter((v) => v.anonymized).length + 1;

  return visitors.map((v) =>
    v.id === id && !v.anonymized
      ? {
          ...v,
          name: `Visitante anonimizado ${index}`,
          phone: '',
          email: '',
          district: '',
          invitedById: null,
          note: '',
          wantsContact: false,
          ownerId: null,
          stage: STAGE_ARCHIVED,
          archivedAt: v.archivedAt ?? at,
          followUps: v.followUps.map((f) => ({
            ...f,
            note: '',
            ownerId: null,
            doneBy: '',
            ...(f.status === 'done' ? {} : { status: 'done' as const, doneAt: at }),
          })),
          contacts: v.contacts.map((c) => ({ ...c, by: '' })),
          anonymized: true,
        }
      : v
  );
}

/** As fichas que passaram do prazo de guarda (a varredura diária do servidor). */
export function anonymizeExpired(
  visitors: Visitor[],
  retentionMonths: number,
  today: string,
  at: number
): { visitors: Visitor[]; ids: string[] } {
  const ids = visitors.filter((v) => retentionDue(v, retentionMonths, today)).map((v) => v.id);

  return { visitors: ids.reduce((list, id) => anonymizeVisitor(list, id, at), visitors), ids };
}

/** Tira o membro (anonimizado ou excluído) das fichas: responsável, quem convidou e tarefas. */
export function withoutMemberInVisitors(visitors: Visitor[], memberId: string): Visitor[] {
  return visitors.map((v) =>
    v.ownerId === memberId || v.invitedById === memberId || v.followUps.some((f) => f.ownerId === memberId)
      ? {
          ...v,
          ownerId: v.ownerId === memberId ? null : v.ownerId,
          invitedById: v.invitedById === memberId ? null : v.invitedById,
          followUps: v.followUps.map((f) => (f.ownerId === memberId ? { ...f, ownerId: null } : f)),
        }
      : v
  );
}

/**
 * A anonimização de um membro nas fichas: ele sai de responsável, de quem convidou e das
 * tarefas; o nome dele em quem registrou ou fez contato vira o anônimo; e a ficha de
 * onde ele veio (o cadastro encaminhado, já aprovado) é anonimizada junto.
 */
export function anonymizeMemberInVisitors(
  visitors: Visitor[],
  memberId: string,
  registrations: readonly Registration[],
  rename: (text: string) => string,
  at: number
): Visitor[] {
  const origin = visitors.filter((v) => visitorMemberId(v, registrations) === memberId).map((v) => v.id);
  const renamed = withoutMemberInVisitors(visitors, memberId).map((v) => ({
    ...v,
    createdBy: rename(v.createdBy),
    contacts: v.contacts.map((c) => ({ ...c, by: rename(c.by) })),
    followUps: v.followUps.map((f) => ({ ...f, doneBy: rename(f.doneBy) })),
  }));

  return origin.reduce((list, id) => anonymizeVisitor(list, id, at), renamed);
}

/**
 * A ficha sem identificação, para os líderes que não gerenciam visitantes: dá para contar
 * (etapas, datas, origem), não para saber quem é.
 */
export function visitorSummaryOf(visitor: Visitor): Visitor {
  return {
    ...visitor,
    name: '',
    phone: '',
    email: '',
    district: '',
    invitedById: null,
    note: '',
    followUps: visitor.followUps.map((f) => ({ ...f, note: '', doneBy: '' })),
    contacts: visitor.contacts.map((c) => ({ ...c, by: '' })),
    createdBy: '',
  };
}

// --- Números -----------------------------------------------------------------------------

export interface VisitorNumbers {
  /** Da contagem dos cultos do período (`null` sem nenhum registro com o número). */
  firstTimeCounted: number | null;
  recurringCounted: number | null;
  /** Fichas com a primeira visita no período. */
  cards: number;
  /** Dessas, as que voltaram (duas visitas ou mais). */
  returned: number;
  /** `returned / cards` (0 a 1); `null` abaixo de `MIN_RETURN_BASE` fichas. */
  returnRate: number | null;
  /** Fichas em andamento hoje (nem arquivadas nem encaminhadas). */
  inProgress: number;
  /** Acompanhamentos abertos com o prazo vencido, hoje. */
  overdue: number;
  /** Fichas sem responsável, em andamento. */
  unassigned: number;
  wantsContact: number;
  wantsMembership: number;
  /** Encaminhadas ao cadastro e as já aprovadas como membro (das fichas do período). */
  referred: number;
  becameMembers: number;
  bySource: { value: VisitorSource; label: string; count: number }[];
  byStage: { key: string; label: string; count: number }[];
}

const inRange = (date: string, r: DateRange) => date >= r.from && date <= r.to;

function sumCounted(events: readonly ScheduleEvent[], key: 'firstTime' | 'recurring'): number | null {
  const values = events.flatMap((e) => (e.audience?.[key] != null ? [e.audience[key]] : []));

  return values.length ? values.reduce((a, b) => a + b, 0) : null;
}

/** Os números da aba: a contagem dos cultos e as fichas, sempre separadas. */
export function visitorNumbers(input: {
  visitors: readonly Visitor[];
  events: readonly ScheduleEvent[];
  registrations: readonly Registration[];
  settings: Pick<Settings, 'visitorJourney'>;
  range: DateRange;
  today: string;
}): VisitorNumbers {
  const { visitors, events, registrations, settings, range, today } = input;
  const services = servicesIn(events, range, today);
  const cards = visitors.filter((v) => inRange(firstVisit(v), range));
  const returned = cards.filter((v) => new Set(v.visits.map((x) => x.date)).size >= 2).length;
  const current = visitors.filter(
    (v) => !v.anonymized && v.stage !== STAGE_ARCHIVED && v.stage !== STAGE_REGISTERED
  );

  return {
    firstTimeCounted: sumCounted(services, 'firstTime'),
    recurringCounted: sumCounted(services, 'recurring'),
    cards: cards.length,
    returned,
    returnRate: cards.length >= MIN_RETURN_BASE ? returned / cards.length : null,
    inProgress: current.length,
    overdue: overdueFollowUps(visitors, today).length,
    unassigned: current.filter((v) => !v.ownerId).length,
    wantsContact: cards.filter((v) => v.wantsContact).length,
    wantsMembership: cards.filter((v) => v.wantsMembership).length,
    referred: cards.filter((v) => v.registrationId).length,
    becameMembers: cards.filter((v) => visitorMemberId(v, registrations)).length,
    bySource: VISITOR_SOURCES.map((s) => ({ ...s, count: cards.filter((v) => v.source === s.value).length })),
    byStage: journeyOf(settings).map((s) => ({
      key: s.key,
      label: s.label,
      count: cards.filter((v) => boardStage(settings, v) === s.key).length,
    })),
  };
}

/**
 * As pendências dos visitantes na Visão geral (para quem gerencia): acompanhamentos
 * atrasados e fichas em andamento sem responsável.
 */
export function visitorInsights(visitors: readonly Visitor[], today: string): Insight[] {
  const out: Insight[] = [];
  const overdue = overdueFollowUps(visitors, today).length;
  const unassigned = visitors.filter(
    (v) => !v.anonymized && !v.ownerId && v.stage !== STAGE_ARCHIVED && v.stage !== STAGE_REGISTERED
  ).length;

  if (overdue) {
    out.push({
      kind: 'visitors',
      type: 'overdue',
      count: overdue,
      text: `${overdue} ${overdue > 1 ? 'acompanhamentos de visitantes estão atrasados' : 'acompanhamento de visitante está atrasado'}`,
    });
  }

  if (unassigned) {
    out.push({
      kind: 'visitors',
      type: 'unassigned',
      count: unassigned,
      text: `${unassigned} ${unassigned > 1 ? 'fichas de visitantes estão' : 'ficha de visitante está'} sem responsável`,
    });
  }

  return out;
}

export interface VisitorMonth {
  key: string;
  short: string;
  label: string;
  /** Contagem dos cultos do mês (`null` sem registro com o número). */
  firstTime: number | null;
  recurring: number | null;
  /** Fichas com a primeira visita no mês. */
  cards: number;
}

/** Mês a mês do período: a contagem dos cultos e as fichas novas. */
export function visitorsByMonth(
  visitors: readonly Visitor[],
  events: readonly ScheduleEvent[],
  range: DateRange,
  today: string
): VisitorMonth[] {
  const months: VisitorMonth[] = [];
  let key = monthKey(range.from);
  const last = monthKey(range.to);
  const services = servicesIn(events, range, today);

  while (key <= last) {
    const [y, m] = key.split('-').map(Number) as [number, number];
    const inMonth = services.filter((e) => monthKey(e.date) === key);

    months.push({
      key,
      short: MONTH_SHORT[m - 1] ?? '',
      label: `${MONTH_LONG[m - 1]} de ${y}`,
      firstTime: sumCounted(inMonth, 'firstTime'),
      recurring: sumCounted(inMonth, 'recurring'),
      cards: visitors.filter((v) => monthKey(firstVisit(v)) === key && inRange(firstVisit(v), range)).length,
    });

    key = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  }

  return months;
}

/** Os cultos em que se pode registrar uma visita: os que já aconteceram, do mais recente. */
export function visitableServices(
  events: readonly ScheduleEvent[],
  today: string,
  days = 60
): ScheduleEvent[] {
  const from = addDays(today, -days);

  return events
    .filter((e) => e.date <= today && e.date >= from)
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
}

/** As fichas de um culto (o painel do evento mostra quantas). */
export function visitorsOfEvent(visitors: readonly Visitor[], eventId: string): Visitor[] {
  return visitors.filter((v) => v.visits.some((x) => x.eventId === eventId));
}

// --- Link público e mensagens ------------------------------------------------------------

/** Caminho do formulário público: `/{slug}/visitante/{token}`. */
export function visitorPath(slug: string, token: string): string {
  return `/${slug}/visitante/${token}`;
}

/** Mensagem de boas-vindas pelo WhatsApp (editável antes de abrir). */
export function visitorWelcomeMessage(input: {
  visitorName: string;
  orgName: string;
  senderName: string;
}): string {
  const first = input.visitorName.trim().split(/\s+/)[0] ?? '';

  return (
    `Olá, ${first}! Aqui é ${input.senderName}, da ${input.orgName}. ` +
    'Foi muito bom ter você com a gente! Se quiser saber mais sobre a igreja ou tiver alguma dúvida, é só responder esta mensagem.'
  );
}

/** Texto para divulgar o link da ficha (no telão, no grupo, no cartão de boas-vindas). */
export function visitorInvite(orgName: string, url: string): string {
  return `Primeira vez na ${orgName}? Deixe seu contato para a gente te receber melhor: ${url}`;
}

export function rotateVisitorLink(token: string, at: number) {
  return { token, active: true, createdAt: at };
}
